import { expandRecurring } from "../Calendar/recurrence";

export function toMillis(value) {
  if (value == null) return null;
  const date = typeof value?.toDate === "function" ? value.toDate()
    : typeof value?.seconds === "number" ? new Date(value.seconds * 1000)
    : new Date(value);
  return Number.isFinite(date.getTime()) ? date.getTime() : null;
}

export function formatPlannerDate(value) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
  }).formatToParts(new Date(value));
  const part = type => parts.find(item => item.type === type)?.value;
  return `${part("day")}/${part("month")}/${part("year")}`;
}

export function formatEventTime(event) {
  const start = toMillis(event?.startUTC);
  const end = toMillis(event?.endUTC);
  if (start == null) return "Date/time pending";
  const time = new Intl.DateTimeFormat("en-CA", {hour: "numeric", minute: "2-digit"});
  const date = formatPlannerDate(start);
  const endDate = end == null ? null : formatPlannerDate(end);
  return `${date} · ${time.format(start)}` + (end == null ? ""
    : ` – ${endDate !== date ? `${endDate} · ` : ""}${time.format(end)}`);
}

export function plannerEventKey(event) {
  return event.instanceDate ? `${event.eventId}__${event.instanceDate}` : event.eventId;
}

export function getPlannerPlan(event, plans) {
  const candidates = [event.sessionPlan, plans[plannerEventKey(event)]]
    .filter(Boolean).sort((a, b) => (toMillis(b.updatedAt) || 0) - (toMillis(a.updatedAt) || 0));
  return candidates[0] || plans[event.eventId] || null;
}

export function getPlannerEvents(rawEvents, {past = false, day = null, now = Date.now()} = {}) {
  const source = (rawEvents || []).filter(event => event?.calendar === "team" && toMillis(event.startUTC) != null);
  const from = day ? new Date(day) : past
    ? new Date(Math.min(now, ...source.map(event => toMillis(event.startUTC)))) : new Date(now);
  from.setHours(0, 0, 0, 0);
  const to = day ? new Date(day) : new Date(now);
  if (!past && !day) to.setFullYear(to.getFullYear() + 1);
  to.setHours(23, 59, 59, 999);
  const events = source.flatMap(event => event.repeat && event.repeat !== "none"
    ? expandRecurring([event], from, to) : [event]);
  return events.filter(event => {
    const start = toMillis(event.startUTC);
    if (day) return start >= +from && start <= +to;
    const end = toMillis(event.endUTC) ?? start;
    return past ? end < now : end >= now;
  }).sort((a, b) => (past ? -1 : 1) * (toMillis(a.startUTC) - toMillis(b.startUTC)));
}

// Read the latest event inside the save transaction. Never recreate a deleted
// event/occurrence or attach a stale plan to a different calendar time.
export function updateEventPlan(events, selected, plan, editMetadata) {
  const source = events.find(event => event.eventId === selected.eventId);
  const occurrence = selected.instanceDate && source
    ? expandRecurring([source], new Date(selected.startUTC), new Date(selected.endUTC))
      .find(event => event.instanceDate === selected.instanceDate)
    : source;
  if (!occurrence || occurrence.calendar !== "team") {
    throw new Error("This calendar session was deleted. Close this plan and select another session.");
  }
  if (toMillis(occurrence.startUTC) !== toMillis(selected.startUTC)
    || toMillis(occurrence.endUTC) !== toMillis(selected.endUTC)) {
    throw new Error("This session was rescheduled. Close and reopen the plan before saving.");
  }
  const patch = {sessionPlanId: plannerEventKey(selected), sessionPlan: plan,
    mapId: plan.mapId, mapName: plan.mapName, ...editMetadata};
  return events.map(event => {
    if (event !== source) return event;
    if (!selected.instanceDate) return {...event, ...patch};
    return {...event, overrides: {...event.overrides, [selected.instanceDate]: {
      ...event.overrides?.[selected.instanceDate],
      startUTC: occurrence.startUTC, endUTC: occurrence.endUTC, ...patch,
    }}};
  });
}
